<script lang="ts">
	import { createEventDispatcher, onMount, onDestroy, beforeUpdate } from 'svelte';
	import { fly } from 'svelte/transition';
	import { mobile } from 'src/services/mobile-rework';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import PromoModalHeader from '../molecules/PromoModalHeader.svelte';

	// ============================================
	// Props - API compatible with DS Modal
	// ============================================

	/** Modal title */
	export let title: string = '';
	/** Whether modal is visible */
	export let showModal: boolean = false;
	/** Whether clicking backdrop closes the modal */
	export let backdropClose: boolean = true;
	/** Custom z-index for the modal */
	export let zIndex: number = 1000;
	/** Hide the title/header area */
	export let hideTitle: boolean = false;
	/** Accessible label for close button */
	export let closeAriaLabel: string = 'Close';
	/** Maximum width of the modal */
	export let maxWidth: number = 600;

	// ============================================
	// Events
	// ============================================

	const dispatch = createEventDispatcher<{ close: void }>();

	const handleClose = () => {
		dispatch('close');
	};

	// ============================================
	// Window size and mobile detection
	// ============================================

	let windowInnerWidth: number;
	let bodyLock = false;

	$: isMobile = windowInnerWidth <= SystemScreenBreakpoints.MOBILE_MAX;

	// ============================================
	// Touch gesture handling for mobile swipe-to-close
	// ============================================

	let isTouched = false;
	let touchStartPosition = 0;
	let touchEndPosition = 0;

	$: move = touchEndPosition - touchStartPosition;
	$: styleTransform = isTouched && isMobile ? `transform:translateY(${move < 0 ? 0 : move}px)` : '';

	const onTouch = (e: TouchEvent) => {
		if (!isMobile) return;
		isTouched = true;
		touchStartPosition = e.targetTouches[0].clientY;
	};

	const onTouchMove = (e: TouchEvent) => {
		if (!isMobile) return;
		touchEndPosition = e.targetTouches[0].clientY;
	};

	const onTouchEnd = (e: TouchEvent) => {
		if (!isMobile) return;
		// If swiped down more than 100px, close the modal
		if (move > 100) {
			handleClose();
		}
		isTouched = false;
		touchEndPosition = 0;
		touchStartPosition = 0;
	};

	// ============================================
	// Body scroll lock
	// ============================================

	const toggleBodyScroll = (destroy = false) => {
		const body = document.body;
		if (showModal && !destroy && !bodyLock) {
			bodyLock = true;
			body.classList.add('modal-open');
		} else if ((bodyLock && !showModal) || destroy) {
			body.classList.remove('modal-open');
			bodyLock = false;
		}
	};

	beforeUpdate(() => {
		toggleBodyScroll();
	});

	// ============================================
	// Mobile back button handling
	// ============================================

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', handleClose);
		}
	});

	onDestroy(() => {
		if (mobile.isMobile) {
			mobile.removeEventListener('back_button', handleClose);
		}
		toggleBodyScroll(true);
	});

	// ============================================
	// Keyboard handling
	// ============================================

	$: if (showModal) {
		if (typeof window !== 'undefined') {
			window.addEventListener('keydown', keyPress);
		}
	} else if (typeof window !== 'undefined') {
		window.removeEventListener('keydown', keyPress);
	}

	const keyPress = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			handleClose();
		}
	};
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

{#if showModal}
	<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
	<div
		class="promo-modal-wrapper"
		style="--z-index: {zIndex};"
		on:click|self={() => backdropClose && handleClose()}
	>
		<div
			class="promo-modal-transition-container"
			class:is-mobile={isMobile}
			in:fly={{ y: isMobile ? 300 : 0, duration: 300 }}
			out:fly={{ y: isMobile ? 100 : 0, duration: 300 }}
		>
			<div
				class="promo-modal-container"
				class:is-mobile={isMobile}
				role="dialog"
				aria-modal="true"
				aria-labelledby={title ? 'promo-modal-title' : undefined}
				style="--max-width: {maxWidth}px; {styleTransform}"
			>
				<!-- Swipe handle touch area (mobile only) -->
				<div
					class="promo-modal-touch-area"
					on:touchstart|passive={onTouch}
					on:touchend={onTouchEnd}
					on:touchmove={onTouchMove}
				>
					<!-- Mobile swipe handle indicator -->
					<div class="promo-modal-swipe-handle">
						<div class="promo-modal-swipe-handle__bar" />
					</div>

					<!-- Header -->
					{#if !hideTitle && title}
						<PromoModalHeader {title} {closeAriaLabel} {isMobile} on:close={handleClose} />
					{/if}
				</div>

				<!-- Main content slot -->
				<div class="promo-modal__content">
					<slot />
				</div>

				<!-- Optional footer slot -->
				{#if $$slots.footer}
					<div class="promo-modal__footer">
						<slot name="footer" />
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.promo-modal-wrapper {
		inset: 0;
		position: fixed;
		z-index: var(--z-index, 1000);
		background: rgba(0, 0, 0, 0.8);
	}

	/* Transition container - handles positioning */
	.promo-modal-transition-container {
		position: fixed;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 2;
		pointer-events: none;
	}

	/* Mobile: bottom sheet positioning */
	.promo-modal-transition-container.is-mobile {
		align-items: flex-end;
	}

	.promo-modal-container {
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: var(--max-width, 600px);
		max-width: min(var(--max-width, 600px), 95%);
		max-height: 85vh;
		background: var(--color-base, #151617);
		border: 1px solid var(--color-3, #5b5f66);
		border-radius: var(--radius-big, 12px);
		overflow: hidden;
		pointer-events: all;
	}

	/* Mobile: bottom sheet container */
	.promo-modal-container.is-mobile {
		max-width: 100%;
		max-height: calc(100% - 16px);
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		border-bottom: none;
		/* padding: 0 5px 16px 6px; */
	}

	/* Touch area for swipe gestures */
	.promo-modal-touch-area {
		flex-shrink: 0;
		padding: 24px 0px;
	}

	/* Swipe handle - hidden on desktop */
	.promo-modal-swipe-handle {
		display: none;
	}

	/* Mobile: show swipe handle */
	.promo-modal-container.is-mobile .promo-modal-swipe-handle {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.promo-modal-swipe-handle__bar {
		margin-bottom: 12px;
		margin-top: -8px;
		width: 40px;
		height: 4px;
		border-radius: var(--radius-small, 4px);
		background: var(--color-3, #5b5f66);
	}

	.promo-modal__content {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
	}

	.promo-modal__footer {
		flex-shrink: 0;
		padding: 24px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-direction: row;
		gap: 16px;
	}

	@media screen and (max-width: 639px) {
		.promo-modal-container .promo-modal__footer {
			padding: 16px 24px 0px 24px;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			height: auto;
		}
	}
</style>
